import { ElementType, Intent } from "@mobility-item-page/utilities/client";
import {
  createContext,
  type Dispatch,
  type MouseEventHandler,
  type ReactNode,
  type RefObject,
  type SetStateAction,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { useAdViewAd } from "src/context/AdViewProviderContext.tsx";
import { useAppConfig } from "src/context/AppConfigProviderContext.tsx";
import usePulse from "src/utils/pulse-tracking/usePulse.ts";
import { Fullscreen } from "./Fullscreen.tsx";
import { hasAdSlide, scrollAdIntoView, scrollIntoView } from "./util.ts";

type EventHandler = (event?: Parameters<MouseEventHandler>[0]) => void;

type GalleryState = {
  fullScreen: boolean;
  setFullScreen: Dispatch<SetStateAction<boolean>>;
  currentImageIndex: number;
  setCurrentImageIndex: (value: number | ((prevValue: number) => number)) => void;
  isOnAdSlide: boolean;
  setIsOnAdSlide: Dispatch<SetStateAction<boolean>>;
  hasOpenedFullscreen: boolean;
  inCarouselAdLoaded: boolean;
  setInCarouselAdLoaded: Dispatch<SetStateAction<boolean>>;
  nonFullscreenAdLoaded: boolean;
  setNonFullscreenAdLoaded: Dispatch<SetStateAction<boolean>>;
  showNextImage: EventHandler;
  showPreviousImage: EventHandler;
  overview: boolean;
  toggleOverview: EventHandler;
  galleryRef: RefObject<HTMLDivElement | null>;
};

const initialState: GalleryState = {
  fullScreen: false,
  setFullScreen: () => {},
  currentImageIndex: 0,
  setCurrentImageIndex: () => {},
  isOnAdSlide: false,
  setIsOnAdSlide: () => {},
  hasOpenedFullscreen: false,
  inCarouselAdLoaded: false,
  setInCarouselAdLoaded: () => {},
  nonFullscreenAdLoaded: false,
  setNonFullscreenAdLoaded: () => {},
  showNextImage: () => {},
  showPreviousImage: () => {},
  overview: false,
  toggleOverview: () => {},
  galleryRef: { current: null },
};

const GalleryContext = createContext<GalleryState>(initialState);

export const useGallery = () => {
  return useContext(GalleryContext);
};

export const GalleryProvider = ({ children }: { children: ReactNode }) => {
  const navigationClicked = useRef({ prev: false, next: false });
  const { initialImage, isMobile } = useAppConfig();
  const [currentImageIndex, setImageIndex] = useState(initialImage ? initialImage - 1 : 0);
  const [isOnAdSlide, setIsOnAdSlide] = useState(false);
  const [hasOpenedFullscreen, setHasOpenedFullscreen] = useState(false);
  const [inCarouselAdLoaded, setInCarouselAdLoaded] = useState(false);
  const [nonFullscreenAdLoaded, setNonFullscreenAdLoaded] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);
  const [overview, setOverview] = useState(false);
  const { images } = useAdViewAd();
  const { trackClick } = usePulse();
  const galleryRef = useRef<HTMLDivElement>(null);

  const isGalleryInView = useCallback(() => {
    if (!galleryRef.current) return false;
    const rect = galleryRef.current.getBoundingClientRect();
    return rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
  }, []);

  const trackViewImage = (direction: "next" | "prev") => {
    // Tracking event: CE_ev06
    const alreadyTracked = navigationClicked.current[direction];
    if (!alreadyTracked) {
      trackClick({
        name: "View carousel image",
        objectId: "CE_ev06",
        elementType: ElementType.Image,
        intent: Intent.View,
      });
      navigationClicked.current[direction] = true;
    }
  };

  const setCurrentImageIndex = (value: number | ((prevValue: number) => number)) => {
    setImageIndex((prev) => {
      const newValue = typeof value === "function" ? value(prev) : value;
      const tempUrl = new URL(window.location.href);
      tempUrl.searchParams.set("ci", (newValue + 1).toString());
      window.history.replaceState(null, "", tempUrl);
      return newValue;
    });
  };

  useEffect(() => {
    if (fullScreen) {
      setHasOpenedFullscreen(true);
    }
  }, [fullScreen]);

  useEffect(() => {
    // Mobile only shows the ad slide while fullscreen, and once fullscreen has been opened
    // once, the non-fullscreen ad slide is hidden for good (remounting it risks the GAM slot
    // for AdvtIncarousel going stale). Either way, closing fullscreen while on the ad slide
    // should drop back to a real image instead of leaving the gallery stuck on a slide that
    // no longer exists.
    if (!fullScreen && isOnAdSlide && (isMobile || hasOpenedFullscreen)) {
      setIsOnAdSlide(false);
    }
  }, [fullScreen, isMobile, isOnAdSlide, hasOpenedFullscreen]);

  const toggleOverview: EventHandler = (event) => {
    event?.stopPropagation();
    setOverview((prev) => !prev);
  };

  const showNextImage: EventHandler = (event) => {
    event?.stopPropagation();

    // Currently on the ad slide -> loop back to image 0
    if (isOnAdSlide) {
      trackViewImage("next");
      setIsOnAdSlide(false);
      setCurrentImageIndex(0);
      scrollIntoView(0);
      return;
    }

    setCurrentImageIndex((currentIndex) => {
      const lastImageIndex = images.length - 1;

      if (currentIndex >= lastImageIndex) {
        trackViewImage("next");

        // On last image with ad active -> go to the ad slide instead of looping
        if (hasAdSlide()) {
          setIsOnAdSlide(true);
          scrollAdIntoView();
          return currentIndex;
        }

        scrollIntoView(0);
        return 0;
      }

      trackViewImage("next");
      const nextIndex = currentIndex + 1;
      scrollIntoView(nextIndex);
      return nextIndex;
    });
  };

  const showPreviousImage: EventHandler = (event) => {
    event?.stopPropagation();

    // Currently on the ad slide -> loop back to the last image
    if (isOnAdSlide) {
      trackViewImage("prev");
      setIsOnAdSlide(false);
      setCurrentImageIndex(images.length - 1);
      scrollIntoView(images.length - 1);
      return;
    }

    setCurrentImageIndex((currentIndex) => {
      const lastImageIndex = images.length - 1;

      if (currentIndex === 0) {
        trackViewImage("prev");

        // On first image with ad active -> go to the ad slide instead of looping
        if (hasAdSlide()) {
          setIsOnAdSlide(true);
          scrollAdIntoView();
          return currentIndex;
        }

        scrollIntoView(lastImageIndex);
        return lastImageIndex;
      }

      trackViewImage("prev");
      const prevIndex = currentIndex - 1;
      scrollIntoView(prevIndex);
      return prevIndex;
    });
  };

  const onKeyDown = (event: KeyboardEvent) => {
    if (event.key === "Escape") {
      setFullScreen(false);
    }
    if (fullScreen) {
      return;
    }

    if (!isGalleryInView()) {
      return;
    }

    if (event.key === "ArrowRight") {
      showNextImage();
    }
    if (event.key === "ArrowLeft") {
      showPreviousImage();
    }
  };

  const keydownHandlerRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keydownHandlerRef.current = onKeyDown;

  useEffect(() => {
    const listener = (e: KeyboardEvent) => keydownHandlerRef.current(e);
    window.addEventListener("keydown", listener);
    return () => {
      window.removeEventListener("keydown", listener);
    };
  }, []);

  return (
    <GalleryContext
      value={{
        fullScreen,
        setFullScreen,
        currentImageIndex,
        setCurrentImageIndex,
        isOnAdSlide,
        setIsOnAdSlide,
        hasOpenedFullscreen,
        inCarouselAdLoaded,
        setInCarouselAdLoaded,
        nonFullscreenAdLoaded,
        setNonFullscreenAdLoaded,
        showNextImage,
        showPreviousImage,
        overview,
        toggleOverview,
        galleryRef,
      }}
    >
      <div ref={galleryRef}>{fullScreen ? <Fullscreen>{children}</Fullscreen> : children}</div>
    </GalleryContext>
  );
};
